import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableBuilder from '../TableBuilder/TableBuilder';
import {
  fetchUsers,
  addUserAsync,
  updateUserAsync,
  deleteUserAsync
} from '../../slices/usersSlice';
import { useNotification } from '../../Helper/NotificationProvider';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { ConfigProvider, Modal, Form, Input, Select, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import store from '../../store';
import { fetchImages } from '../../slices/imagesSlice';

const Users = () => {
  const root = document.documentElement;
  const primaryColor = getComputedStyle(root)
    .getPropertyValue('--primary-color')
    .trim();

  // Redux hooks
  const dispatch = useDispatch();
  const users = useSelector(state => state.users.data);
  const loading = useSelector(state => state.users.loading);
  const user = store.getState().auth.user;

  const navigate = useNavigate();
  const { openNotification, contextHolder } = useNotification();
  const { t } = useTranslation();

  // Local state for modal visibility
  const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
  const [form] = Form.useForm();

  const columns = [
    { title: 'Benutzername', dataIndex: 'username', editable: true },
    { title: 'Email', dataIndex: 'email', editable: true },
    { title: 'Rolle',
      dataIndex: 'role',
      editable: true,
      render: (text, record) => {
          if(text === 'admin')
            return "Admin"
          if(text === 'event_manager')
            return "Event Manager"
        if(text === 'user')
          return "User"

      },
    },
    { title: 'Erstellt', dataIndex: 'createdAt' },
    { title: 'Geändert', dataIndex: 'updatedAt' }
  ];

  const typFilter = ['Name/John Doe', 'City/New York', 'Name/Jane Smith'];
  const searchableColumns = ['firstName'];
  const sortableColumns = [
    'firstName',
    'email',
    'role',
    'createdAt',
    'updatedAt'
  ];
  const columnColors = [{ columnName: 'firstName', color: 'primary-color' }];

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch, navigate]);

  const handleAddUser = async () => {
    try {
      const values = await form.validateFields();
      const resultAction = await dispatch(addUserAsync(values))
      if(addUserAsync.rejected.match(resultAction)) {
        openNotification(
          'bottomRight',
          "Fehler",
          'Benutzer konnte nicht erstellen werden. Bitte versuchen Sie es erneut.',
          false
        );
      } else {
        dispatch(fetchUsers());
        openNotification(
          'bottomRight',
          'Erfolg',
          'Benutzer erfolgreich hinzugefügt.',
          true
        );
      }
      setIsAddUserModalVisible(false);
      form.resetFields();

    } catch (error) {
      openNotification('bottomRight', 'Fehler', error.message, false);
    }
  };

  const handleEditUser = (key, row) => {
    // Dispatch edit action or call API to update data here
    try {
      dispatch(updateUserAsync({ key, ...row })).then(() => {
        dispatch(fetchUsers());
        openNotification(
          'bottomRight',
          'Erfolg',
          'Benutzer wurde erfolgreich geändert',
          true
        );
      });
    } catch (error) {
      openNotification(
        'bottomRight',
        'Fehler',
        'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
        false
      );
    }
  };

  const handleDeleteUser = key => {
    // Dispatch delete action or call API to delete data here
    try {
      dispatch(deleteUserAsync(key));
    } catch (error) {
      openNotification(
        'bottomRight',
        'Fehler',
        'Benutzer konnte nicht gelöscht werden.',
        error.message
      );
    }
  };

  return (
    <>
      {contextHolder}
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: primaryColor
          },
          components: {
            Typography: {
              titleMarginTop: '0.5em'
            }
          }
        }}
      >
        <div
          style={{
            margin: '15px',
            marginTop: '10px'
          }}
        >
          <Button
            type="primary"
            onClick={() => setIsAddUserModalVisible(true)}
            icon={<PlusOutlined />}
            style={{ backgroundColor: 'black', marginBottom: 20 }}
          >
            {t('Benutzer hinzufügen')}
          </Button>
          <TableBuilder
            title={'Benutzer'}
            data={users}
            columns={columns}
            searchableColumns={searchableColumns}
            sortableColumns={sortableColumns}
            columnColors={columnColors}
            typfilter={typFilter}
            expandable={false}
            loading={loading}
            handleSave={handleEditUser}
            handleDelete={handleDeleteUser}
          />
          <br />
        </div>
      </ConfigProvider>

      <Modal
        title={t('Benutzer hinzufügen')}
        visible={isAddUserModalVisible}
        onOk={handleAddUser}
        onCancel={() => setIsAddUserModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label={t('Benutzername')}
            rules={[
              { required: true, message: t('Bitte Benutzername eingeben') }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('Email')}
            rules={[
              { required: true, message: t('Please enter the email') },
              { type: 'email', message: t('Please enter a valid email') }
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label={t('Rolle')}
            rules={[{ required: true, message: t('Bitte Rolle auswählen') }]}
          >
            <Select>
              <Select.Option value="user">User</Select.Option>
              <Select.Option value="event_manager">Event Manager</Select.Option>
              {user?.role === "admin" &&
                <Select.Option value="admin">Admin</Select.Option>
              }
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Users;
